import { useContext } from 'react';
import { BaseWidget } from './BaseWidget';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { FontControl } from '../controls/grouped/FontControl';
import { ColorControl } from '../controls/ColorControl';
import useSettings from '../../../../hooks/Settings';
import { TemplateDataContext } from '../context/TemplateDataContext';
import { ReportBrandingContext } from '../context/ReportBrandingContext';

/**
 * CompanyNameWidget — renders the organisation's display name (CommunityName),
 * resolved dynamically (never stored on the widget) so a template instantiated
 * by any tenant shows that tenant's own name. Resolution priority:
 *   1. template preview → templateData.data.branding.compagnyName (exportTemplateData)
 *   2. public viewer     → ReportBrandingContext.compagnyName (owner name from CF)
 *   3. editor / in-app   → useSettings().compagnyName (logged-in tenant)
 *
 * @extends {BaseWidget}
 */
export class CompanyNameWidget extends BaseWidget {
    getName() { return 'Bedrijfsnaam'; }
    getIcon() { return <BusinessOutlinedIcon />; }

    getControls() {
        return {
            font: new FontControl({
                label: 'Lettertype',
                description: 'Instellingen voor het lettertype van de bedrijfsnaam',
                defaultValue: {
                    fontSize: { value: 24, unit: 'px' },
                    alignment: 'left',
                },
            }),
            color: new ColorControl({
                label: 'Kleur',
                description: 'Kleur van de bedrijfsnaam',
                defaultValue: '#000000',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {object} [props.font] - FontControl value (fontSize, alignment, weight, etc.)
 * @param {string} [props.color] - Text colour
 */
const Renderer = ({ font, color }) => {
    const { templateData, isTemplate } = useContext(TemplateDataContext);
    const branding = useContext(ReportBrandingContext);
    const settingsName = useSettings().compagnyName;

    const name = isTemplate
        ? (templateData?.data?.branding?.compagnyName || '')
        : (branding?.compagnyName || settingsName || '');

    const style = {
        textAlign: font?.alignment,
        fontFamily: font?.fontFamily,
        fontSize: font?.fontSize && `${font.fontSize.value}${font.fontSize.unit}`,
        fontStyle: font?.fontStyle,
        fontWeight: font?.fontWeight,
        textDecoration: font?.textDecoration,
        color: color,
    };

    return <p style={style}>{name}</p>;
};
